import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useUser, useCoinHistory, type CoinTransaction } from "../lib/api";
import Layout from "../components/Layout";
import { useTranslation } from "../hooks/useTranslation";
import { formatNumber } from "../utils/formatNumber";

const COIN_AMOUNTS = {
  referral: 50,
  follower: 5,
  post: 2,
  like: 2,
  repost: 3,
} as const;

const typeLabelKey: Record<string, string> = {
  referral: "coins.referral",
  follower: "coins.follower",
  post: "coins.post",
  like: "coins.like",
  repost: "coins.repost",
};

const EARN_ITEMS = [
  { key: "referral" as const, amount: COIN_AMOUNTS.referral },
  { key: "follower" as const, amount: COIN_AMOUNTS.follower },
  { key: "post" as const, amount: COIN_AMOUNTS.post },
  { key: "like" as const, amount: COIN_AMOUNTS.like },
  { key: "repost" as const, amount: COIN_AMOUNTS.repost },
];

// Animated counter for game-like number roll
function AnimatedCoins({ value }: { value: number }) {
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    const start = display;
    const end = value;
    if (start === end) return;
    const duration = 400;
    const startTime = performance.now();
    const step = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - t, 2);
      setDisplay(Math.round(start + (end - start) * easeOut));
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [value]);
  return <span>{formatNumber(display)}</span>;
}

// 3D-style coin visual (CSS perspective + gradient)
function Coin3D({ size = 48 }: { size?: number }) {
  return (
    <motion.div
      className="relative flex items-center justify-center"
      style={{ width: size, height: size, perspective: "200px" }}
      animate={{ rotateY: [0, 360] }}
      transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
    >
      <div
        className="absolute rounded-full border-2 shadow-lg"
        style={{
          width: size,
          height: size,
          background: "linear-gradient(145deg, #f59e0b 0%, #d97706 40%, #b45309 60%, #fbbf24 100%)",
          boxShadow: "inset -2px -4px 8px rgba(0,0,0,0.3), inset 2px 2px 6px rgba(255,255,255,0.2), 0 6px 20px rgba(245,158,11,0.4)",
          borderColor: "rgba(251,191,36,0.6)",
        }}
      >
        <div
          className="absolute rounded-full border border-amber-200/40"
          style={{
            inset: "18%",
            background: "linear-gradient(145deg, rgba(255,255,255,0.25) 0%, transparent 50%)",
          }}
        />
      </div>
    </motion.div>
  );
}

const HISTORY_PER_PAGE = 8;

const CoinsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [page, setPage] = useState(1);
  const [allHistory, setAllHistory] = useState<CoinTransaction[]>([]);
  const lastAppliedPageRef = useRef(0);
  const { user } = useAuth();
  const { data: userData } = useUser(user?.id?.toString() || "", {
    enabled: !!user?.id,
  });
  const { data: pageData, isLoading, isFetching } = useCoinHistory(page, HISTORY_PER_PAGE, {
    enabled: !!user?.id,
  });
  const currentPageData = Array.isArray(pageData) ? pageData : [];

  // Only merge when the request for the current page has finished (no refresh of existing items)
  useEffect(() => {
    if (isFetching) return;
    if (page === 1) {
      setAllHistory(currentPageData);
      lastAppliedPageRef.current = 1;
      return;
    }
    if (page > lastAppliedPageRef.current && currentPageData.length > 0) {
      setAllHistory((prev) => [...prev, ...currentPageData]);
      lastAppliedPageRef.current = page;
    }
  }, [page, currentPageData, isFetching]);

  const hasMore = currentPageData.length >= HISTORY_PER_PAGE;
  const isLoadingMore = isFetching && page > 1;
  const history = allHistory;

  const coins = userData?.coins ?? 0;
  const referralUrl =
    typeof window !== "undefined" && userData?.referral_code
      ? `${window.location.origin}/signup?ref=${userData.referral_code}`
      : "";

  const handleCopyInviteLink = useCallback(() => {
    if (!referralUrl) return;
    navigator.clipboard.writeText(referralUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [referralUrl]);

  if (!user?.id) {
    return (
      <Layout title={t("coins.title")} description={t("coins.howToEarn")}>
        <div className="min-h-screen bg-[#0f0f12] flex items-center justify-center px-4">
          <p className="text-amber-200/80 text-center">
            برای مشاهده سکه‌ها وارد شوید.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title={`${t("coins.title")} - لنزسه`}
      description={t("coins.howToEarn")}
      noIndex
    >
      <div className="min-h-screen bg-[#0f0f12] text-white overflow-hidden">
        {/* Game-style background */}
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-950/20 via-transparent to-transparent" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-amber-500/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-0 w-[300px] h-[300px] bg-amber-600/5 rounded-full blur-[80px]" />
        </div>

        <div className="relative max-w-2xl mx-auto px-4 py-6 pb-24">
          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex items-center justify-between mb-6"
          >
            <motion.button
              onClick={() => navigate(-1)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 text-amber-200/90 hover:text-amber-100 p-2 -m-2 rounded-xl transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium">{t("common.back")}</span>
            </motion.button>
            <h1 className="text-lg font-bold text-amber-100/90 tracking-tight">
              {t("coins.title")}
            </h1>
            <div className="w-16" />
          </motion.header>

          {/* Balance card — 3D tilt + floating coin */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="relative mb-8"
          >
            <motion.div
              className="relative rounded-3xl overflow-hidden border border-amber-500/30 bg-gradient-to-br from-amber-900/40 via-amber-950/50 to-stone-900/80 p-6 pb-7"
              style={{
                boxShadow: "0 0 0 1px rgba(251,191,36,0.1), 0 20px 50px -12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
              }}
              whileHover={{ scale: 1.01 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-amber-300/80 mb-3 text-xs font-medium uppercase tracking-wider mb-1">
                    {t("coins.yourCoins")}
                  </p>
                  <p className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-amber-100 to-yellow-200 tabular-nums">
                    <AnimatedCoins value={coins} /> <span className="text-amber-400/90 text-2xl md:text-3xl">سکه</span>
                  </p>
                  <p className="text-amber-400/60 text-sm mt-2 max-w-[200px]">
                    {t("coins.useCoins")}
                  </p>
                </div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
                >
                  <Coin3D size={72} />
                </motion.div>
              </div>

              {referralUrl && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  transition={{ delay: 0.3 }}
                  className="mt-5 pt-5 border-t border-amber-500/20"
                >
                  <p className="text-amber-300/70 text-xs mb-3">
                    {t("coins.inviteLinkSubtitle")}
                  </p>
                  <motion.button
                    type="button"
                    onClick={handleCopyInviteLink}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    className="w-full py-3 px-4 rounded-2xl font-bold text-amber-950 bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-400 shadow-lg shadow-amber-500/25 hover:shadow-amber-400/30 transition-shadow"
                  >
                    <AnimatePresence mode="wait">
                      {copied ? (
                        <motion.span
                          key="copied"
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0.9, opacity: 0 }}
                        >
                          {t("coins.copied")}
                        </motion.span>
                      ) : (
                        <motion.span
                          key="copy"
                          initial={{ scale: 0.9 }}
                          animate={{ scale: 1 }}
                        >
                          {t("coins.copyInviteLink")}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </motion.button>
                </motion.div>
              )}
            </motion.div>
          </motion.section>

          {/* Link to coin leaderboard */}
          <motion.button
            type="button"
            onClick={() => navigate("/coins/leaderboard")}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="w-full mb-8 py-3 px-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200/90 font-medium transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            {t("coins.viewLeaderboard")}
          </motion.button>

          {/* How to earn — quest cards */}
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="mb-8"
          >
            <h2 className="text-sm font-bold text-amber-200/90 uppercase tracking-wider mb-4">
              {t("coins.howToEarn")}
            </h2>
            <ul className="space-y-3">
              {EARN_ITEMS.map(({ key, amount }, i) => (
                <motion.li
                  key={key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * i, type: "spring", stiffness: 200, damping: 20 }}
                >
                  <motion.div
                    className="flex items-center gap-4 p-4 rounded-2xl border border-amber-500/20 bg-stone-900/60 backdrop-blur-sm"
                    style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.2)" }}
                    whileHover={{ scale: 1.01, borderColor: "rgba(245,158,11,0.35)" }}
                    whileTap={{ scale: 0.99 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                  >
                    <motion.span
                      className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-amber-950 font-black text-lg bg-gradient-to-br from-amber-400 to-amber-600 shadow-md"
                      whileHover={{ rotate: [0, -8, 8, 0], transition: { duration: 0.4 } }}
                    >
                      {amount}+
                    </motion.span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-amber-50">
                        {t(typeLabelKey[key] || key)}
                      </p>
                      <p className="text-sm text-amber-300/70">
                        {t(typeLabelKey[key] + "Desc", { amount })}
                      </p>
                    </div>
                  </motion.div>
                </motion.li>
              ))}
            </ul>
          </motion.section>

          {/* Recent history */}
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
          >
            <h2 className="text-sm font-bold text-amber-200/90 uppercase tracking-wider mb-4">
              {t("coins.recentHistory")}
            </h2>
            {isLoading ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-center py-12"
              >
                <motion.div
                  className="w-10 h-10 rounded-full border-2 border-amber-500/50 border-t-amber-400"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                />
              </motion.div>
            ) : !history?.length ? (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-amber-400/50 py-8 text-center text-sm"
              >
                {t("coins.noHistory")}
              </motion.p>
            ) : (
              <>
                <ul className="space-y-2">
                  {(history as CoinTransaction[]).map((tx, i) => (
                    <motion.li
                      key={tx.id}
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 * Math.min(i, 12) }}
                    >
                      <motion.div
                        className="flex items-center justify-between py-3 px-4 rounded-xl bg-stone-900/50 border border-amber-500/10"
                        whileTap={{ scale: 0.99 }}
                      >
                        <span className="text-amber-100/90 text-sm">
                          {t(typeLabelKey[tx.type] || tx.type)}
                        </span>
                        <span className="text-amber-400 font-bold tabular-nums">
                          +{tx.amount}
                        </span>
                      </motion.div>
                    </motion.li>
                  ))}
                </ul>
                {hasMore && (
                  <div className="mt-6 text-center">
                    <motion.button
                      type="button"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={isLoadingMore}
                      className="px-5 py-2.5 rounded-xl bg-amber-500/20 border border-amber-500/30 text-amber-200/90 text-sm font-medium hover:bg-amber-500/30 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {isLoadingMore ? t("common.loading") : t("common.loadMore")}
                    </motion.button>
                  </div>
                )}
              </>
            )}
          </motion.section>
        </div>
      </div>
    </Layout>
  );
};

export default CoinsPage;
